import * as React from "react"
import { useDropzone } from "react-dropzone"
import { Upload, FileText, AlertCircle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "./button"

interface DropzoneProps {
  onDrop: (acceptedFiles: File[]) => void
  accept?: Record<string, string[]>
  maxSize?: number
  className?: string
  disabled?: boolean
}

export function Dropzone({
  onDrop,
  accept = {
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt'],
    'application/json': ['.json']
  },
  maxSize = 10 * 1024 * 1024, // 10MB
  className,
  disabled = false
}: DropzoneProps) {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections
  } = useDropzone({
    onDrop,
    accept,
    maxSize,
    disabled,
    multiple: false
  })

  return (
    <div className={cn("w-full", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
          isDragActive && !isDragReject && "border-primary bg-primary/5",
          isDragReject && "border-destructive bg-destructive/5",
          disabled && "opacity-50 cursor-not-allowed",
          "hover:border-primary/50"
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
            {isDragActive ? (
              <Upload className="w-8 h-8 text-primary" />
            ) : (
              <FileText className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium">
              {isDragActive
                ? isDragReject
                  ? "File type not supported"
                  : "Drop the file here"
                : "Drag & drop a file here"}
            </p>
            <p className="text-sm text-muted-foreground">
              or click to select a file
            </p>
            <p className="text-xs text-muted-foreground">
              Supports PDF, TXT, and JSON files up to 10MB
            </p>
          </div>
          {!isDragActive && (
            <Button variant="outline" size="sm">
              Choose File
            </Button>
          )}
        </div>
      </div>
      
      {fileRejections.length > 0 && (
        <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2 text-destructive">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">File rejected</span>
          </div>
          <ul className="mt-2 text-sm text-destructive/80">
            {fileRejections.map(({ file, errors }) => (
              <li key={file.name}>
                {file.name}: {errors.map(e => e.message).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
} 